import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';
import { isDoctor, isNurse } from '@/auth/roles';
import { AccountSettingsPage } from './account-settings-page';
import { NurseAccountSettingsPage } from './components/nurse-account-settings-page';

/**
 * Account Settings route — role dispatch (PROD-1200 / PROD-1206, epic PROD-1199).
 *
 * Mirrors Flutter `base_screen.dart:28-57`: the profile menu's "Account
 * Settings" opens the logged-in user's OWN self-edit surface:
 *   - doctor → DoctorBaseWidget(isFromAccountSettings: true) → AccountSettingsPage
 *   - nurse  → StaffBaseWidget(isFromAccountSettings: true)  → NurseAccountSettingsPage
 *
 * `user.id` is the setup-record GUID (doctorSetupId / staffSetupId). Any other
 * role has no self-edit surface and bounces home with a reason (mirrors PM's
 * unsupported-role bounce).
 */
export function AccountSettingsRoute() {
  const user = useAuthStore((s) => s.user);
  const selfId = user?.id;
  const doctor = isDoctor(user) && !!selfId;
  const nurse = isNurse(user) && !!selfId;
  const willRedirect = !doctor && !nurse;

  useEffect(() => {
    if (!willRedirect) return;
    console.warn(
      `[AccountSettingsRoute] No self-edit surface for role "${user?.role ?? 'unknown'}" — redirecting home.`,
    );
    toast.info('Account Settings isn’t available for your role.');
  }, [willRedirect, user?.role]);

  if (doctor) {
    return <AccountSettingsPage doctorSetupId={selfId} />;
  }
  if (nurse) {
    return <NurseAccountSettingsPage staffSetupId={selfId} />;
  }
  return <Navigate to="/" replace />;
}
